/**
 * Fil d'étapes partagé par les fenêtres de saisie du dossier apprenant.
 *
 * L'inscription et la modification saisissent les mêmes trente rubriques : les
 * présenter d'affilée décourage la saisie, et les découper différemment d'un
 * écran à l'autre obligerait à réapprendre le même formulaire deux fois.
 */

/**
 * Fil des étapes. Les étapes déjà traversées restent cliquables : corriger une
 * coquille de la première étape depuis la dernière ne doit pas demander de tout
 * reparcourir. Sur mobile, les intitulés cèdent la place au seul numéro.
 */
export function Progression({
  etapes,
  courante,
  onAller,
}: {
  etapes: string[];
  courante: number;
  onAller: (index: number) => void;
}): JSX.Element {
  return (
    <div className="border-b border-gray-100 px-4 pb-3 pt-3 sm:px-6">
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {etapes.map((etape, index) => {
          const active = index === courante;
          const passee = index < courante;
          return (
            <li key={etape} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onAller(index)}
                aria-current={active ? 'step' : undefined}
                className={`flex min-h-[44px] items-center gap-2 rounded-lg px-2 py-1 text-xs transition ${
                  active
                    ? 'font-semibold text-codialis-navy'
                    : 'text-codialis-gray hover:text-codialis-navy'
                }`}
              >
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                    active
                      ? 'bg-codialis-navy text-white'
                      : passee
                        ? 'bg-codialis-mint text-codialis-navy'
                        : 'bg-gray-100 text-codialis-gray'
                  }`}
                >
                  {passee ? <Icone trace={TRACE_COCHE} classe="h-3.5 w-3.5" /> : index + 1}
                </span>
                <span className={active ? '' : 'hidden sm:inline'}>{etape}</span>
              </button>
              {index < etapes.length - 1 && (
                <span className="hidden h-px w-4 bg-gray-200 sm:block" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>
      <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full bg-codialis-mint transition-all"
          style={{ width: `${((courante + 1) / etapes.length) * 100}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Pictogrammes en SVG inline, comme partout ailleurs dans le projet :
 * `lucide-react` n'est pas une dépendance du front, et l'ajouter pour quatre
 * icônes ne se justifie pas. Les tracés sont ceux de Lucide.
 */
export function Icone({ trace, classe }: { trace: string; classe: string }): JSX.Element {
  return (
    <svg
      className={classe}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d={trace} />
    </svg>
  );
}

export const TRACE_FLECHE_GAUCHE = 'M19 12H5m0 0l7 7m-7-7l7-7';
export const TRACE_FLECHE_DROITE = 'M5 12h14m0 0l-7-7m7 7l-7 7';
export const TRACE_COCHE = 'M20 6L9 17l-5-5';
export const TRACE_ALERTE =
  'M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z';
